/**
 * PROVISIONAL ICM contracts for Sales Admin (MVP Shopping 2.1.1 / 2.1.2).
 * Maps to Intershop customer price lists + custom catalogs conceptually.
 */

/**
 * Priority order:
 * 1 sku_flat → 2 sku_percent → 3 manufacturer → 4 catalog → 5 list.
 * Legacy level `sku` is accepted in stored/demo data and normalized by overrideType.
 */
export type SalesAdminPriceLevel =
  | 'sku_flat'
  | 'sku_percent'
  | 'manufacturer'
  | 'catalog'
  | 'list'
  /** @deprecated Prefer sku_flat / sku_percent */
  | 'sku';

export type SalesAdminPriceOverrideType = 'flat' | 'percent';

export type SalesAdminPriceRuleLevel = Exclude<SalesAdminPriceLevel, 'list'>;

/**
 * Account price list bucket (prototype).
 * Each account has four ICM-mapped lists: P1–P4. Users assign into a priority;
 * list names are internal and not shown in the Sales Admin UI.
 *
 * Assumptions (flag for review, not confirmed MVP):
 * - Most recently saved rule wins same-priority overlaps.
 * - Category SKU selections are snapshots of products at save time.
 * - Percent discounts apply to list price; currency is USD in the prototype.
 */
export interface SalesAdminManagedPriceList {
  id: string;
  customerNo: string;
  /** Internal label only — not displayed in the assign UI. */
  name: string;
  /** ICM priority bucket this list represents. */
  priority: 1 | 2 | 3 | 4;
  updatedAt: string;
  updatedBy: string;
}

export interface SalesAdminManagedPriceListPayload {
  name: string;
}

export interface SalesAdminPriceRule {
  id: string;
  customerNo: string;
  /** Owning P1–P4 account price list. */
  priceListId: string;
  /**
   * Internal label only (auto-derived from target). Not an ICM field —
   * ICM entries are identified by SKU / list, not an assignment name.
   */
  name: string;
  level: SalesAdminPriceRuleLevel;
  /** Derived from level for sku_flat/sku_percent; manufacturer/catalog are percent only. */
  overrideType: SalesAdminPriceOverrideType;
  /** @deprecated Prefer `skus` — kept for single-SKU / CSV rows. */
  sku?: string;
  /** SKUs for sku_flat and sku_percent rules (multi-select / category snapshot). */
  skus?: string[];
  /** Optional category used when building a P2 SKU snapshot (exclusions applied into `skus`). */
  categoryId?: string;
  manufacturer?: string;
  catalogId?: string;
  flatPrice?: number;
  percentOff?: number;
  active: boolean;
  updatedAt: string;
  updatedBy: string;
}

export interface SalesAdminPriceRulePayload {
  /** Optional; omitted in UI — mock/ICM mapping derives identity from target fields. */
  name?: string;
  level: SalesAdminPriceRuleLevel;
  overrideType: SalesAdminPriceOverrideType;
  priceListId?: string;
  sku?: string;
  skus?: string[];
  categoryId?: string;
  manufacturer?: string;
  catalogId?: string;
  flatPrice?: number;
  percentOff?: number;
  active?: boolean;
}

export interface SalesAdminSkuSuggestion {
  sku: string;
  name: string;
}

export interface SalesAdminCatalog {
  id: string;
  customerNo: string;
  name: string;
  description?: string;
  skus: string[];
  updatedAt: string;
  updatedBy: string;
}

export interface SalesAdminCatalogPayload {
  name: string;
  description?: string;
  skus: string[];
}

export interface SalesAdminUploadResult {
  created: number;
  updated: number;
  errors: string[];
}

export interface SalesAdminListResponse<T> {
  elements: T[];
}

/** Input for client-side / demo price resolution (priority 1–5). */
export interface SalesAdminPriceResolveInput {
  sku: string;
  manufacturer?: string;
  listPrice: number;
  /** Warehouse held stock — always resolves to $0. */
  warehouseHeld?: boolean;
  /** Need-pricing / RFQ items keep list semantics (no override applied). */
  needPricing?: boolean;
  rules: SalesAdminPriceRule[];
  catalogs: SalesAdminCatalog[];
}

export interface SalesAdminPriceResolveResult {
  unitPrice: number;
  level: SalesAdminPriceLevel;
  ruleId?: string;
  catalogId?: string;
  note: string;
}

/** Normalize legacy `sku` level + collect skus from sku/skus fields. */
export function normalizeSalesAdminPriceRuleLevel(
  level: SalesAdminPriceRuleLevel | string | undefined,
  overrideType?: SalesAdminPriceOverrideType
): Exclude<SalesAdminPriceLevel, 'list' | 'sku'> | undefined {
  if (level === 'sku_flat' || level === 'sku_percent' || level === 'manufacturer' || level === 'catalog') {
    return level;
  }
  if (level === 'sku') {
    return overrideType === 'percent' ? 'sku_percent' : 'sku_flat';
  }
  return undefined;
}

export function salesAdminPriceRuleSkus(rule: Pick<SalesAdminPriceRule, 'sku' | 'skus'>): string[] {
  const fromList = (rule.skus || []).map(s => String(s).trim()).filter(Boolean);
  if (fromList.length) {
    return Array.from(new Set(fromList));
  }
  const single = (rule.sku || '').trim();
  return single ? [single] : [];
}

export function salesAdminPriceRuleMatchesSku(
  rule: Pick<SalesAdminPriceRule, 'sku' | 'skus'>,
  sku: string
): boolean {
  const upper = (sku || '').trim().toUpperCase();
  if (!upper) {
    return false;
  }
  return salesAdminPriceRuleSkus(rule).some(s => s.toUpperCase() === upper);
}
